import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { TradingAccountPreview } from '@pages/Trade/TradingAccountInfo/components/TradingAccountPreview'
import { Account } from '@pages/Trade/types'

import { BaseModalType } from '@context/ModalsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { Body2 } from '@ui/Typography'
import { Modal } from '@ui/components/Modal'
import {
  SegmentedControl,
  SegmentedControlTab,
} from '@ui/components/SegmentedControl/SegmentedControl'
import { Loader } from '@ui/components/Spinner/Loader'

import { useAccountActionAccessCheck } from '@utils/AccountAccess/accountAccessCheck'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { CommonPaths } from '@utils/path-common'

import { AccountGroupType } from '@model/AccountGroupType'
import { PlatformTypeEnum } from '@model/PlatformTypeEnum'
import { DemoTradingAccount, TradingAccount, TradingAccountStatusType } from '@model/TradingAccount'

import { safeLocalStorage } from '@hooks/useStorage'

import { Box } from '@shared/components/Box'

interface AccountsModalProps {
  selectedAccount: Account
  onAccountChange: (account: Account) => void
}
export enum SwitchAccountTabEnum {
  Live = 'live',
  Demo = 'demo',
}
export interface SwitchAccountTab {
  id: SwitchAccountTabEnum
  name: React.ReactNode
  onClick?: () => void
}
export const AccountsModal: BaseModalType<AccountsModalProps> = (props) => {
  const { close, selectedAccount, onAccountChange } = props
  const apiClient = useApiClient(ClientApiClient)

  const [liveAccounts, setLiveAccounts] = useState<TradingAccount[]>([])
  const [demoAccounts, setDemoAccounts] = useState<DemoTradingAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<SwitchAccountTabEnum>(SwitchAccountTabEnum.Live)
  const navigate = useNavigate()
  const { checkAccountOpenAccountAccess } = useAccountActionAccessCheck()
  const { addSnackbar } = useSnackbar()
  const { t } = useTranslation()
  useEffect(() => {
    if (!selectedAccount) return
    setActiveTab(
      selectedAccount.accountGroupType?.id === AccountGroupType.Live
        ? SwitchAccountTabEnum.Live
        : SwitchAccountTabEnum.Demo
    )
  }, [selectedAccount])
  const addNewAccountButton = () => {
    close()
    const path =
      activeTab === SwitchAccountTabEnum.Demo
        ? CommonPaths.NewDemoTradingAccount
        : CommonPaths.NewLiveTradingAccount
    checkAccountOpenAccountAccess(() => navigate(path))
  }

  const isTMTorTVAccount = (account: Account) =>
    account.platformType?.id === PlatformTypeEnum.TickmillTrader ||
    account.platformType?.id === PlatformTypeEnum.TradingView

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        setLoading(true)

        const statusSearch = `(Status == ${TradingAccountStatusType.Approved}) OR (Status == ${TradingAccountStatusType.CloseOnly})`

        const [liveResponse, demoResponse] = await Promise.all([
          apiClient.getTradingAccounts({
            pageSize: 1000,
            pageIndex: 1,
            search: statusSearch,
          }),
          apiClient.getDemoTradingAccounts({
            pageSize: 1000,
            pageIndex: 1,
            search: statusSearch,
          }),
        ])

        setLiveAccounts(liveResponse.items || [])
        setDemoAccounts(demoResponse.items || [])
      } catch (error) {
        console.error('Failed to load accounts:', error)
      } finally {
        setLoading(false)
      }
    }

    loadAccounts()
  }, [apiClient])

  const sortedAccounts = useMemo(() => {
    const accounts = activeTab === SwitchAccountTabEnum.Live ? liveAccounts : demoAccounts

    if (!selectedAccount) return accounts

    const selectedId = selectedAccount.id

    return [
      ...accounts.filter((acc) => acc.id === selectedId),
      ...accounts.filter((acc) => acc.id !== selectedId),
    ]
  }, [activeTab, liveAccounts, demoAccounts, selectedAccount])
  const switchAccountTabs: SegmentedControlTab[] = [
    {
      id: SwitchAccountTabEnum.Live,
      name: t('Trading Account.Live'),
      onClick: () => setActiveTab(SwitchAccountTabEnum.Live),
    },
    {
      id: SwitchAccountTabEnum.Demo,
      name: t('Trading Account.Demo'),
      onClick: () => setActiveTab(SwitchAccountTabEnum.Demo),
    },
  ]

  const changeAccount = (account: Account) => {
    if (account.id === selectedAccount.id) {
      return
    }

    onAccountChange(account)
    close()

    if (!isTMTorTVAccount(account)) {
      return
    }

    try {
      safeLocalStorage.setItem('lastLoggedInTMTAccountId', account.id)
      addSnackbar.success({
        message: t('Markets.SwitchedAccount', {
          account: `${account.name}`,
        }),
        closable: true,
      })
    } catch (error) {
      console.error('Failed to save TMT account:', error)
    }
  }

  return (
    <Modal>
      <Modal.Header canClose>{t('Markets.TradingAccounts')}</Modal.Header>
      <Modal.Body>
        <Box fullWidth>
          <Box mb='x16' flex direction='column' gap='x16'>
            <div>
              <SegmentedControl size='md' activeTab={activeTab} tabs={switchAccountTabs} />
            </div>
            <Box fullWidth flex align='center' justify='center'>
              {loading ? (
                <Loader size={'xs'}></Loader>
              ) : sortedAccounts.length === 0 ? (
                <Body2>{t('Markets.NoAccountsFound')}</Body2>
              ) : (
                <Box flex direction='column' gap='x12' fullWidth>
                  {sortedAccounts.map((account) => (
                    <TradingAccountPreview
                      key={account.id}
                      onClick={changeAccount}
                      account={account}
                      isSelected={String(account.id) === String(selectedAccount.id)}
                      onDeposit={close}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Primary onClick={addNewAccountButton}>
          {t('Markets.AddNewAccount')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
